import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_IDLE_MS = 15 * 60 * 1000;
const DEFAULT_WARNING_MS = 60 * 1000;
const ACTIVITY_EVENTS: readonly (keyof WindowEventMap)[] = [
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
];

export interface UseSessionTimeoutOptions {
  /** Idle duration before the session is considered expired. */
  idleMs?: number;
  /** How long before expiry the warning countdown starts. */
  warningMs?: number;
  /** Called once the idle duration elapses. */
  onExpire: () => void;
  /** Disable the timer entirely (e.g. no authenticated session). */
  enabled?: boolean;
}

export interface UseSessionTimeoutResult {
  /** True while the warning countdown is showing. */
  isWarning: boolean;
  /** Seconds remaining before expiry, only meaningful while `isWarning`. */
  secondsRemaining: number;
  /** Reset the idle timer, e.g. from a "stay signed in" action. */
  extendSession: () => void;
}

/**
 * Tracks user activity and fires `onExpire` after `idleMs` of inactivity,
 * showing a warning for the last `warningMs`. Does not itself clear any
 * query cache — call `queryClient.clear()` (or similar) from `onExpire`.
 */
export function useSessionTimeout({
  idleMs = DEFAULT_IDLE_MS,
  warningMs = DEFAULT_WARNING_MS,
  onExpire,
  enabled = true,
}: UseSessionTimeoutOptions): UseSessionTimeoutResult {
  const [isWarning, setIsWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(warningMs / 1000));

  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const tickIntervalRef = useRef<ReturnType<typeof setInterval>>();

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(expiryTimerRef.current);
    clearInterval(tickIntervalRef.current);
  }, []);

  const extendSession = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    setIsWarning(false);
    setSecondsRemaining(Math.ceil(warningMs / 1000));

    const warnAfter = Math.max(idleMs - warningMs, 0);
    warningTimerRef.current = setTimeout(() => {
      setIsWarning(true);
      let remaining = Math.ceil(warningMs / 1000);
      setSecondsRemaining(remaining);
      tickIntervalRef.current = setInterval(() => {
        remaining -= 1;
        setSecondsRemaining(Math.max(remaining, 0));
      }, 1000);
    }, warnAfter);

    expiryTimerRef.current = setTimeout(() => {
      clearTimers();
      onExpire();
    }, idleMs);
  }, [clearTimers, enabled, idleMs, onExpire, warningMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Deferred to the next tick so the timer setup doesn't set state
    // synchronously within the effect body (see react-hooks/set-state-in-effect).
    const startId = setTimeout(extendSession, 0);
    const handleActivity = () => {
      if (!isWarning) extendSession();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, handleActivity);
    }

    return () => {
      clearTimeout(startId);
      clearTimers();
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, handleActivity);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { isWarning, secondsRemaining, extendSession };
}

/** Convenience wrapper that also clears the TanStack Query cache on expiry. */
export function useSessionTimeoutWithCacheClear(
  options: Omit<UseSessionTimeoutOptions, "onExpire"> & { onExpire?: () => void },
): UseSessionTimeoutResult {
  const queryClient = useQueryClient();

  return useSessionTimeout({
    ...options,
    onExpire: () => {
      queryClient.clear();
      options.onExpire?.();
    },
  });
}
